import { useState, useEffect } from 'react'
import { useAdminAuth } from '../../context/AdminAuthContext'
import { Menu, Bell, Search, Moon, Sun } from 'lucide-react'
import { useLocation, useNavigate } from 'react-router-dom'

const PAGE_TITLES = {
  '/dashboard': 'Dashboard',
  '/orders': 'Orders',
  '/products': 'Products',
  '/customers': 'Customers',
  '/reports': 'Reports',
  '/forecasting': 'Forecasting',
  '/sales-history': 'Sales History',
  '/scheduler': 'Scheduler',
  '/settings': 'Settings',
}

function useDarkMode() {
  const [dark, setDark] = useState(() => {
    const stored = localStorage.getItem('admin-theme')
    if (stored) return stored === 'dark'
    return true // admin defaults to dark
  })

  useEffect(() => {
    const root = document.documentElement
    if (dark) {
      root.classList.add('dark')
      root.classList.remove('admin-light')
    } else {
      root.classList.remove('dark')
      root.classList.add('admin-light')
    }
    localStorage.setItem('admin-theme', dark ? 'dark' : 'light')
  }, [dark])

  return [dark, setDark]
}

export default function TopBar({ onMenuClick }) {
  const { user } = useAdminAuth()
  const location = useLocation()
  const navigate = useNavigate()
  const [searchVal, setSearchVal] = useState('')
  const [dark, setDark] = useDarkMode()

  const title = PAGE_TITLES[location.pathname] ||
    Object.entries(PAGE_TITLES).find(([k]) => location.pathname.startsWith(k))?.[1] ||
    'Admin'

  const handleSearch = (e) => {
    if (e.key === 'Enter' && searchVal.trim()) {
      navigate(`/products?search=${encodeURIComponent(searchVal.trim())}`)
      setSearchVal('')
    }
  }

  return (
    <header className={`h-16 border-b flex items-center px-4 lg:px-6 gap-4 sticky top-0 z-30 backdrop-blur transition-colors duration-200 ${
      dark
        ? 'bg-surface-dark/80 border-surface-border'
        : 'bg-white/90 border-gray-200'
    }`}>
      {/* Mobile menu toggle */}
      <button
        onClick={onMenuClick}
        className={`lg:hidden transition-colors p-1 ${dark ? 'text-gray-400 hover:text-white' : 'text-gray-500 hover:text-gray-900'}`}
      >
        <Menu size={20} />
      </button>

      {/* Page title */}
      <div className="flex items-center gap-3">
        <h1 className={`font-heading text-lg tracking-wide ${dark ? 'text-white' : 'text-gray-900'}`}>{title}</h1>
        <div className={`hidden sm:block w-px h-4 ${dark ? 'bg-surface-border' : 'bg-gray-200'}`} />
        <span className={`hidden sm:block text-xs font-mono ${dark ? 'text-gray-500' : 'text-gray-400'}`}>
          {new Date().toLocaleDateString('en-ZA', { weekday: 'short', day: 'numeric', month: 'short' })}
        </span>
      </div>

      {/* Spacer */}
      <div className="flex-1" />

      {/* Search (desktop) — Enter navigates to Products */}
      <div className={`hidden md:flex items-center gap-2 border rounded-lg px-3 py-2 w-64 transition-colors ${
        dark
          ? 'bg-black/30 border-surface-border'
          : 'bg-gray-100 border-gray-200'
      }`}>
        <Search size={14} className={dark ? 'text-gray-500 shrink-0' : 'text-gray-400 shrink-0'} />
        <input
          type="text"
          value={searchVal}
          onChange={e => setSearchVal(e.target.value)}
          onKeyDown={handleSearch}
          placeholder="Search products... ↵"
          className={`bg-transparent text-sm placeholder-gray-500 outline-none flex-1 ${dark ? 'text-white' : 'text-gray-800'}`}
        />
        <kbd className={`text-[10px] px-1.5 py-0.5 rounded font-mono ${dark ? 'text-gray-600 bg-surface-border/80' : 'text-gray-400 bg-gray-200'}`}>↵</kbd>
      </div>

      {/* Dark / Light toggle */}
      <button
        onClick={() => setDark(d => !d)}
        className={`p-2 transition-colors rounded-lg ${dark ? 'text-gray-400 hover:text-white hover:bg-white/5' : 'text-gray-500 hover:text-gray-900 hover:bg-gray-100'}`}
        title={dark ? 'Switch to light mode' : 'Switch to dark mode'}
      >
        {dark ? <Sun size={18} /> : <Moon size={18} />}
      </button>

      {/* Notifications */}
      <button className={`relative p-2 transition-colors rounded-lg ${dark ? 'text-gray-400 hover:text-white hover:bg-white/5' : 'text-gray-500 hover:text-gray-900 hover:bg-gray-100'}`}>
        <Bell size={18} />
        <span className="absolute top-1.5 right-1.5 w-2 h-2 rounded-full bg-primary border border-surface-dark animate-pulse" />
      </button>

      {/* Avatar */}
      <div className="w-8 h-8 rounded-full bg-primary/40 border border-primary/20 flex items-center justify-center text-sm font-heading text-white cursor-pointer hover:border-primary/50 transition-colors">
        {user?.first_name?.[0] || user?.username?.[0] || 'A'}
      </div>
    </header>
  )
}
